// Chakra imports
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Text,
  Tooltip,
  useClipboard,
  useColorModeValue,
} from "@chakra-ui/react";
import { CheckIcon, CopyIcon } from "@chakra-ui/icons";
import React from "react";
import VerifiedTick from "../../../../components/VerifiedTick/VerifiedTick";

const Header = ({
  backgroundHeader,
  backgroundProfile,
  avatarImage,
  name,
  email,
  code,
  verified,
  tabs,
}) => {
  // Chakra color mode
  const textColor = useColorModeValue("gray.700", "white");
  const borderProfileColor = useColorModeValue(
    "white",
    "rgba(255, 255, 255, 0.31)"
  );
  const emailColor = useColorModeValue("gray.400", "gray.300");
  const codeBg = useColorModeValue("hsla(0,0%,100%,.55)", "whiteAlpha.200");
  const codeBorder = useColorModeValue("gray.200", "whiteAlpha.300");

  // Copyable school-code chip — flips the icon to a check for a moment on copy.
  const { hasCopied, onCopy } = useClipboard(code || "");
  return (
    <Box
      mb={{ sm: "205px", md: "75px", xl: "70px" }}
      borderRadius='15px'
      px='0px'
      display='flex'
      flexDirection='column'
      justifyContent='center'
      align='center'>
      <Box
        bgImage={backgroundHeader}
        w='100%'
        h='300px'
        borderRadius='25px'
        bgPosition='50%'
        bgRepeat='no-repeat'
        position='relative'
        display='flex'
        justifyContent='center'>
        <Flex
          direction={{ sm: "column", md: "row" }}
          mx='1.5rem'
          maxH='330px'
          w={{ sm: "90%", xl: "95%" }}
          justifyContent={{ sm: "center", md: "space-between" }}
          align='center'
          backdropFilter='saturate(200%) blur(50px)'
          position='absolute'
          boxShadow='0px 2px 5.5px rgba(0, 0, 0, 0.02)'
          border='2px solid'
          borderColor={borderProfileColor}
          bg={backgroundProfile}
          p='24px'
          borderRadius='20px'
          transform={{
            sm: "translateY(45%)",
            md: "translateY(110%)",
            lg: "translateY(160%)",
          }}>
          <Flex
            align='center'
            mb={{ sm: "10px", md: "0px" }}
            direction={{ sm: "column", md: "row" }}
            w={{ sm: "100%" }}
            textAlign={{ sm: "center", md: "start" }}>
            <Avatar
              me={{ md: "22px" }}
              src={avatarImage}
              w='80px'
              h='80px'
              borderRadius='15px'
            />
            <Flex direction='column' maxWidth='100%' my={{ sm: "14px" }}>
              <HStack
                spacing='6px'
                align='center'
                justify={{ sm: "center", md: "flex-start" }}>
                <Text
                  fontSize={{ sm: "lg", lg: "xl" }}
                  color={textColor}
                  fontWeight='bold'
                  ms={{ sm: "8px", md: "0px" }}>
                  {name}
                </Text>
                <VerifiedTick tier={verified} size='lg' />
              </HStack>
              <Text
                fontSize={{ sm: "sm", md: "md" }}
                color={emailColor}
                fontWeight='semibold'>
                {email}
              </Text>
              {code && (
                <Tooltip
                  label={hasCopied ? "Copied!" : "Copy school code"}
                  placement='top'
                  hasArrow>
                  <HStack
                    as='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopy();
                    }}
                    alignSelf={{ sm: "center", md: "flex-start" }}
                    mt='6px'
                    spacing='6px'
                    bg={codeBg}
                    border='1px solid'
                    borderColor={hasCopied ? "green.300" : codeBorder}
                    borderRadius='full'
                    px='10px'
                    py='3px'
                    transition='all 0.15s ease'
                    _hover={{ borderColor: "teal.300" }}
                    cursor='pointer'>
                    <Text fontSize='xs' color={emailColor} fontWeight='semibold'>
                      Code:
                    </Text>
                    <Text
                      fontSize='xs'
                      color={textColor}
                      fontWeight='bold'
                      letterSpacing='0.5px'>
                      {code}
                    </Text>
                    <Icon
                      as={hasCopied ? CheckIcon : CopyIcon}
                      boxSize='11px'
                      color={hasCopied ? "green.400" : emailColor}
                    />
                  </HStack>
                </Tooltip>
              )}
            </Flex>
          </Flex>
          <Flex
            direction={{ sm: "column", lg: "row" }}
            w={{ sm: "100%", md: "50%", lg: "auto" }}>
            <Button p='0px' bg='transparent' _hover={{ bg: "none" }} onClick={()=>tabs[0].onClick()}>
              <Flex
                align='center'
                w={{ sm: "100%", lg: "135px" }}
                bg='hsla(0,0%,100%,.3)'
                borderRadius='15px'
                justifyContent='center'
                py='10px'
                boxShadow='inset 0 0 1px 1px hsl(0deg 0% 100% / 90%), 0 20px 27px 0 rgb(0 0 0 / 5%)'
                border='1px solid gray.200'
                cursor='pointer'>
                {tabs[0].icon}
                <Text
                  fontSize='xs'
                  color={textColor}
                  fontWeight='bold'
                  ms='6px'>
                  {tabs[0].name}
                </Text>
              </Flex>
            </Button>
            <Button p='0px' bg='transparent' _hover={{ bg: "none" }} onClick={()=>tabs[1].onClick()}>
              <Flex
                align='center'
                w={{ lg: "135px" }}
                borderRadius='15px'
                justifyContent='center'
                py='10px'
                mx={{ lg: "1rem" }}
                cursor='pointer'>
                {tabs[1].icon}
                <Text
                  fontSize='xs'
                  color={textColor}
                  fontWeight='bold'
                  ms='6px'>
                  {tabs[1].name}
                </Text>
              </Flex>
            </Button>
            <Button p='0px' bg='transparent' _hover={{ bg: "none" }} onClick={()=>tabs[2].onClick()}>
              <Flex
                align='center'
                w={{ lg: "135px" }}
                borderRadius='15px'
                justifyContent='center'
                py='10px'
                cursor='pointer'>
                {tabs[2].icon}
                <Text
                  fontSize='xs'
                  color={textColor}
                  fontWeight='bold'
                  ms='6px'>
                  {tabs[2].name}
                </Text>
              </Flex>
            </Button>

            <Button p='0px' bg='transparent' _hover={{ bg: "none" }} onClick={()=>tabs[3].onClick()}>
              <Flex
                  align='center'
                  w={{ lg: "135px" }}
                  borderRadius='15px'
                  justifyContent='center'
                  py='10px'
                  cursor='pointer'>
                {tabs[3].icon}
                <Text
                    fontSize='xs'
                    color={textColor}
                    fontWeight='bold'
                    ms='6px'>
                  {tabs[3].name}
                </Text>
              </Flex>
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default Header;
